import React from 'react';
import { ArrowLeft, PlayCircle, FileText, BarChart3, CheckCircle2, ArrowRight } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface HowToUsePageProps {
  onBack: () => void;
  onNavigate?: (page: string) => void;
}

export const HowToUsePage: React.FC<HowToUsePageProps> = ({ onBack, onNavigate }) => {
  useSEOMetaTags({
    title: 'How to Use Orcaseotools SEO & AEO Audit Tools | Step-by-Step Guide',
    description: 'Learn how to use Orcaseotools SEO, AEO, and GEO optimization tools. Complete step-by-step guide for website analysis, keyword research, competitor analysis, and SEO reporting. Perfect for beginners and professionals.',
    keywords: 'how to use SEO tools, SEO audit tutorial, AEO tools guide, website SEO analysis guide, keyword research tutorial, SEO tools tutorial, how to use SEO software, SEO audit step by step, SEO tools guide',
    canonicalUrl: 'https://orcaseotools.com/how-to-use',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Use Orcaseotools SEO and AEO Audit Tools",
      "description": "Step-by-step guide on how to use Orcaseotools for SEO, AEO, and GEO optimization",
      "image": "https://orcaseotools.com/logo.png",
      "totalTime": "PT10M",
      "estimatedCost": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": "0"
      },
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Create Account",
          "text": "Sign up for a free Orcaseotools account to get started. No credit card required.",
          "url": "https://orcaseotools.com"
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Enter Website URL",
          "text": "Enter your website URL to begin the comprehensive SEO audit. Our system will analyze over 100 SEO factors.",
          "url": "https://orcaseotools.com/tools"
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Review Results",
          "text": "Analyze the comprehensive SEO report and recommendations. Get actionable insights to improve your website's search engine rankings.",
          "url": "https://orcaseotools.com/dashboard"
        }
      ],
      "url": "https://orcaseotools.com/how-to-use",
      "inLanguage": "en-US"
    }
  });

  const steps = [
    {
      number: '01',
      title: 'Create Your Free Account',
      description: 'Sign up for Orcaseotools with your email address. No credit card required. Get 10 free credits to start analyzing your website immediately.',
      icon: CheckCircle2,
      details: [
        'Visit orcaseotools.com and click "Get Started"',
        'Enter your name, email, and create a password',
        'Verify your email address',
        'Start with 10 free credits for SEO audits'
      ]
    },
    {
      number: '02',
      title: 'Enter Your Website URL',
      description: 'Simply paste your website URL into the audit tool. Our system will automatically crawl and analyze your entire website for SEO issues.',
      icon: FileText,
      details: [
        'Navigate to the SEO Audit tool',
        'Paste your website URL (e.g., https://yoursite.com)',
        'Select your target country and language',
        'Click "Start Audit" to begin analysis'
      ]
    },
    {
      number: '03',
      title: 'Wait for Analysis (2-5 minutes)',
      description: 'Our AI-powered system analyzes your website in real-time. You\'ll see progress updates as we check technical SEO, on-page optimization, performance, and more.',
      icon: PlayCircle,
      details: [
        'Watch real-time progress updates',
        'Analysis typically takes 2-5 minutes',
        'System checks 100+ SEO factors',
        'Receive instant preliminary results'
      ]
    },
    {
      number: '04',
      title: 'Review Your SEO Report',
      description: 'Get a comprehensive SEO report with detailed scores, actionable recommendations, and prioritized issues. Export as PDF for client presentations.',
      icon: BarChart3,
      details: [
        'View overall SEO score (0-100)',
        'Check category scores (Technical, On-Page, Content, etc.)',
        'Review prioritized issues by severity',
        'Export professional PDF report'
      ]
    },
    {
      number: '05',
      title: 'Implement Recommendations',
      description: 'Follow our step-by-step recommendations to fix SEO issues. Each recommendation includes detailed explanations and implementation guides.',
      icon: ArrowRight,
      details: [
        'Start with Critical issues first',
        'Follow detailed fix instructions',
        'Re-run audit to verify improvements',
        'Track progress over time'
      ]
    },
    {
      number: '06',
      title: 'Use Additional Tools',
      description: 'Leverage our full suite of SEO tools including keyword research, competitor analysis, rank tracking, and AEO optimization for complete SEO success.',
      icon: CheckCircle2,
      details: [
        'Research high-value keywords',
        'Analyze competitor strategies',
        'Track keyword rankings',
        'Optimize for AEO and GEO'
      ]
    }
  ];

  const tools = [
    {
      name: 'SEO Audit Tool',
      description: 'Comprehensive website SEO analysis',
      steps: ['Enter URL', 'Wait for analysis', 'Review report', 'Fix issues']
    },
    {
      name: 'Keyword Research',
      description: 'Find profitable keywords for your content',
      steps: ['Enter seed keyword', 'Review suggestions', 'Analyze metrics', 'Export list']
    },
    {
      name: 'Competitor Analysis',
      description: 'Analyze competitor SEO strategies',
      steps: ['Enter competitor URL', 'Compare metrics', 'Identify gaps', 'Create strategy']
    },
    {
      name: 'Rank Tracking',
      description: 'Monitor your keyword rankings',
      steps: ['Add keywords', 'Select location', 'Track positions', 'View reports']
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="space-y-12">
          <header className="text-center space-y-4">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              How to Use Orcaseotools SEO & AEO Audit Tools
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Complete step-by-step guide to using Orcaseotools for professional SEO, AEO, and GEO optimization. Perfect for beginners and advanced users worldwide.
            </p>
          </header>

          <section className="space-y-8">
            <h2 className="text-3xl font-bold text-center">Getting Started: 6 Simple Steps</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div
                    key={index}
                    className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 space-y-4 relative"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl font-bold text-blue-400">{step.number}</span>
                        </div>
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <Icon size={20} className="text-blue-400" />
                          <h3 className="text-xl font-bold text-white">{step.title}</h3>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">{step.description}</p>
                        <ul className="space-y-2">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="text-slate-400 text-xs flex items-start gap-2">
                              <CheckCircle2 size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border border-blue-500/30 rounded-2xl p-8 space-y-6">
            <h2 className="text-3xl font-bold text-center">Quick Start Guides for Each Tool</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tools.map((tool, index) => (
                <div key={index} className="bg-slate-800/50 rounded-xl p-6 space-y-4">
                  <h3 className="text-xl font-bold text-white">{tool.name}</h3>
                  <p className="text-slate-300 text-sm">{tool.description}</p>
                  <div className="space-y-2">
                    {tool.steps.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                        <span className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-4">
            <h2 className="text-2xl font-bold">Tips for Best Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h3 className="font-bold text-indigo-400">For SEO Audits:</h3>
                <ul className="space-y-1 text-sm text-slate-300">
                  <li>• Run audits regularly (weekly/monthly)</li>
                  <li>• Fix critical issues first</li>
                  <li>• Compare results over time</li>
                  <li>• Share reports with your team</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h3 className="font-bold text-indigo-400">For Keyword Research:</h3>
                <ul className="space-y-1 text-sm text-slate-300">
                  <li>• Focus on commercial intent keywords</li>
                  <li>• Analyze competition before targeting</li>
                  <li>• Track long-tail keywords</li>
                  <li>• Update keyword lists regularly</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="text-center space-y-4">
            <h2 className="text-2xl font-bold">Ready to Get Started?</h2>
            <p className="text-slate-300">
              Create your free account and start analyzing your website in minutes. No credit card required.
            </p>
            <button 
              onClick={() => onNavigate ? onNavigate('landing') : onBack()}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-lg transition-colors"
            >
              Start Free Trial Now
            </button>
          </section>
        </article>
      </div>
    </div>
  );
};

